import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { LoginDialog } from './LoginDialog';
import { RegisterDialog } from './RegisterDialog';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleSignIn } from './GoogleSignIn';

interface NavbarProps {
  language: string;
  setLanguage: (lang: string) => void;
}

const Navbar = ({ language, setLanguage }: NavbarProps) => {
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout: authLogout, login } = useAuth();
  const t = translations[language === 'ku' ? 'ku' : 'en'];
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);

  const { promptGoogleSignIn } = useGoogleSignIn({
    onSuccess: () => {
      handleLoginClose();
      handleRegisterClose();
    }
  });

  // Add this useEffect to show Google Sign-In popup
  useEffect(() => {
    // Only show the popup if user is not logged in
    if (!user) {
      // Add a delay to avoid immediate popup which might be annoying
      const timer = setTimeout(() => {
        promptGoogleSignIn();
      }, 2000); // 2 second delay

      return () => clearTimeout(timer);
    }
  }, [user, promptGoogleSignIn]); // Dependencies

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleLoginOpen = () => {
    setLoginOpen(true);
  };

  const handleLoginClose = () => {
    setLoginOpen(false);
  };

  const handleRegisterOpen = () => {
    setRegisterOpen(true);
  };

  const handleRegisterClose = () => {
    setRegisterOpen(false);
  };

  const handleGoogleButtonClick = () => {
    promptGoogleSignIn();
  };

  const handleLogout = () => {
    authLogout();
    handleProfileClose();
    navigate('/');
  };

  const handleLoginSuccess = (userData: any) => {
    // This will now be handled by the context
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{ 
          backgroundColor: '#F4F1EA',
          color: '#382110',
          boxShadow: 'none',
          borderBottom: '1px solid #D8D8D8',
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            padding: { xs: 1, sm: 2 },
          }}
        >
          {/* Top section with logo and profile */}
          <Box sx={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 1, sm: 0 }
          }}>
            {/* Logo */}
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                fontFamily: '"Noto Sans Arabic", "Arial Unicode MS", system-ui, sans-serif',
                fontWeight: 'bold',
                fontSize: { xs: '1.2rem', sm: '1.5rem' },
              }}
            >
              {t.appName}
            </Typography>

            {/* Profile Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user ? (
                <>
                  <IconButton color="inherit" size="small">
                    <NotificationsIcon />
                  </IconButton>
                  <IconButton color="inherit" size="small">
                    <MessageIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleProfileClick}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <Avatar 
                      sx={{ width: 32, height: 32 }}
                      src={user.profile_image_url}
                    >
                      {user.name.charAt(0)}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={profileAnchorEl}
                    open={Boolean(profileAnchorEl)}
                    onClose={handleProfileClose}
                    onClick={handleProfileClose}
                  >
                    <MenuItem 
                      component={Link} 
                      to={`/users/${user?.id}`}
                    >
                      {t.menuProfile}
                    </MenuItem>
                    <MenuItem
                      component={Link} 
                      to={`/books?user_id=${user?.id}`}
                    >
                      {t.menuMyBooks}
                    </MenuItem>
                    <MenuItem
                      component={Link} 
                      to={`/settings`}
                    >
                      {t.menuSettings}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>{t.menuSignOut}</MenuItem>
                  </Menu>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    color="inherit"
                    onClick={handleLoginOpen}
                    sx={{ 
                      textTransform: 'none',
                      fontSize: { xs: '12px', sm: '14px' },
                      padding: { xs: '4px 8px', sm: '6px 16px' },
                    }}
                  >
                    {t.signin}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleRegisterOpen}
                    sx={{ 
                      backgroundColor: '#382110',
                      color: '#FFFFFF',
                      textTransform: 'none',
                      fontSize: { xs: '12px', sm: '14px' },
                      padding: { xs: '4px 8px', sm: '6px 16px' },
                      '&:hover': {
                        backgroundColor: '#58371F',
                      },
                    }}
                  >
                    {t.join}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {/* Bottom section with navigation */}
          <Box sx={{ 
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 2 },
            alignItems: 'center'
          }}>
            {/* Navigation Links */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-around', sm: 'flex-end' }
            }}>
              {/* Desktop Menu */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                <Button
                  color="inherit"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={(e) => setLanguageAnchorEl(e.currentTarget)}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 'normal',
                  }}
                >
                  {t.menuLanguage}
                </Button>
                <Menu
                  anchorEl={languageAnchorEl}
                  open={Boolean(languageAnchorEl)}
                  onClose={() => setLanguageAnchorEl(null)}
                >
                  <MenuItem onClick={() => {
                    setLanguage('en');
                    localStorage.setItem('language', 'en');
                    setLanguageAnchorEl(null);
                  }}>
                    English
                  </MenuItem>
                  <MenuItem onClick={() => {
                    setLanguage('ku');
                    localStorage.setItem('language', 'ku');
                    setLanguageAnchorEl(null);
                  }}>
                    کوردی
                  </MenuItem>
                </Menu>
                <Button
                  color="inherit"
                  href="/authors"
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 'normal',
                  }}
                >
                  {t.menuAuthors}
                </Button>
              </Box>

              {/* Mobile Menu */}
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMobileMenuOpen}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchorEl}
                  open={Boolean(mobileMenuAnchorEl)}
                  onClose={handleMobileMenuClose}
                  sx={{ 
                    mt: 1,
                    '& .MuiPaper-root': {
                      width: '200px',
                    }
                  }}
                >
                  <MenuItem onClick={() => {
                    setLanguage('en');
                    localStorage.setItem('language', 'en');
                    handleMobileMenuClose();
                  }}>
                    English
                  </MenuItem>
                  <MenuItem onClick={() => {
                    setLanguage('ku');
                    localStorage.setItem('language', 'ku');
                    handleMobileMenuClose();
                  }}>
                    کوردی
                  </MenuItem>
                  <MenuItem 
                    component={Link} 
                    to="/authors"
                    onClick={handleMobileMenuClose}
                  >
                    {t.menuAuthors}
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <LoginDialog
        open={loginOpen}
        onClose={handleLoginClose}
        onSuccess={handleLoginSuccess}
        onSwitchToRegister={() => {
          handleLoginClose();
          handleRegisterOpen();
        }}
        translations={t}
        handleGoogleButtonClick={handleGoogleButtonClick}
      />

      <RegisterDialog
        open={registerOpen}
        onClose={handleRegisterClose}
        onSuccess={handleLoginSuccess}
        onSwitchToLogin={() => {
          handleRegisterClose();
          handleLoginOpen();
        }}
        translations={t}
        handleGoogleButtonClick={handleGoogleButtonClick}
      />
    </>
  );
};

// Add TypeScript interface for window object to include Google
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: (notification?: string) => void;
          renderButton: (
            element: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              width?: number;
            }
          ) => void;
          cancel: () => void;
        };
      };
    };
  }
}

export default Navbar; 